import React, { useEffect, useRef, useState } from "react";
import { adminGetJson, adminPostJson } from "./adminApi";
import { AdminSpinner } from "./AdminLoadingUi";
import { buttonStyle, cardStyle, chipStyle, muted, primaryButtonStyle, ResultsTable, textareaStyle, useElapsed, WELCOME_QUESTIONS, parseMessageRawData } from "./AdminIntelligenceShared";

function answerMeta(raw, message) {
  return {
    rowCount: raw?.row_count ?? (Array.isArray(raw?.rows) ? raw.rows.length : null),
    executionMs: raw?.execution_ms ?? message?.execution_ms ?? null,
    model: raw?.vertex?.sql_generation_location || raw?.vertex?.narrative_location || message?.token_usage?.narrative?.model_used || message?.token_usage?.sql_generation?.model_used || "gemini",
  };
}

function AssistantBubble({ message, onEntityClick }) {
  const [showSql, setShowSql] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const raw = parseMessageRawData(message) || {};
  const meta = answerMeta(raw, message);
  return (
    <div style={{ display: "flex", justifyContent: "flex-start" }}>
      <div style={{ ...cardStyle, width: "min(100%, 920px)", padding: 16 }}>
        <div style={{ fontSize: 16, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{message.content}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          {meta.rowCount != null ? <span style={chipStyle}>{meta.rowCount} rows</span> : null}
          {meta.executionMs != null ? <span style={chipStyle}>{meta.executionMs} ms</span> : null}
          <span style={chipStyle}>{meta.model}</span>
        </div>
        {message.sql_generated ? (
          <div style={{ marginTop: 12 }}>
            <button style={buttonStyle} onClick={() => setShowSql((v) => !v)}>{showSql ? "Hide SQL" : "View SQL"}</button>
            {showSql ? <pre style={{ marginTop: 10, padding: 14, background: "#0f172a", color: "#e2e8f0", borderRadius: 14, overflowX: "auto", fontSize: 12, lineHeight: 1.5 }}>{message.sql_generated}</pre> : null}
          </div>
        ) : null}
        {Array.isArray(raw.rows) ? (
          <div style={{ marginTop: 12 }}>
            <button style={buttonStyle} onClick={() => setShowTable((v) => !v)}>{showTable ? "Hide data table" : "View data table"}</button>
            {showTable ? <div style={{ marginTop: 10 }}><ResultsTable rows={raw.rows} onEntityClick={onEntityClick} /></div> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Architecture: propMessages as source of truth, gated by session identity.
//
// POST /chat creates the job and returns immediately. The server processes
// all stages in the background (fire-and-forget). The client polls
// GET /sessions/:id?advance=false every 2.5s until the job reaches
// "complete" or "error". This avoids the Heroku 30s request timeout that
// killed the old single-GET approach.
//
// messagesReady / pendingUserText / freshAssistantMsg: same optimistic
// overlay pattern as before — show the user's question and the assistant's
// answer immediately, suppress once propMessages confirms them.
// ─────────────────────────────────────────────────────────────────────────────

export default function AdminTalkToDataTab({ sessionId, session, messages: propMessages, sessionLoading, onSessionStarted, onSessionResolved, onEntityClick, onNewSession }) {
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [pendingUserText, setPendingUserText] = useState("");
  const [freshAssistantMsg, setFreshAssistantMsg] = useState(null);
  const ownAskRef = useRef(false);
  const listRef = useRef(null);
  const lastAnswerRef = useRef(null);
  // Tracks the session being polled so we can cancel if the user navigates away.
  const activeAskRef = useRef(null);
  const elapsed = useElapsed(submitting);

  // Cancel polling and clear overlays when navigating away via sidebar.
  useEffect(() => {
    if (ownAskRef.current) {
      ownAskRef.current = false;
      return;
    }
    activeAskRef.current = null; // cancel any running poll
    setPendingUserText("");
    setFreshAssistantMsg(null);
    setError("");
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cancel polling on unmount (tab switch / resetNonce change).
  useEffect(() => { return () => { activeAskRef.current = null; }; }, []);

  // ── Build display thread ──────────────────────────────────────────────────
  // Only use propMessages when Firestore has confirmed they belong to THIS session.
  const messagesReady = Boolean(sessionId && session?.id === sessionId);
  const baseMessages = messagesReady ? (propMessages || []) : [];

  // Suppress optimistic user bubble once propMessages already contains it.
  const showPendingUser = Boolean(
    pendingUserText &&
    !baseMessages.some((m) => m.role === "user" && m.content === pendingUserText)
  );
  // Suppress optimistic assistant bubble once propMessages already contains it.
  const showFreshAssistant = Boolean(
    freshAssistantMsg &&
    !baseMessages.some((m) => m.id === freshAssistantMsg.id)
  );

  const displayThread = [
    ...baseMessages,
    ...(showPendingUser ? [{ id: "_pu", role: "user", content: pendingUserText }] : []),
    ...(showFreshAssistant ? [freshAssistantMsg] : []),
  ];

  const isEmpty = !displayThread.length && !submitting;
  const lastAssistantIdx = displayThread.reduce((acc, m, i) => m.role === "assistant" ? i : acc, -1);
  // Show the loading overlay when the sidebar session is loading AND we have not
  // yet received Firestore-confirmed messages for this session.
  // Only show loading overlay for sidebar navigation — never when ask() just
  // finished and we have optimistic content (pendingUserText / freshAssistantMsg).
  const showLoadingOverlay = (sessionLoading || (sessionId && !messagesReady)) && !submitting && !pendingUserText && !freshAssistantMsg;

  // ── Scroll: top for empty, bottom when asking, answer-start when answer arrives ──
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (isEmpty) { el.scrollTop = 0; return; }
    if (submitting) {
      // User just asked — scroll to bottom so they see their question + "Thinking..."
      el.scrollTop = el.scrollHeight;
    } else if (lastAnswerRef.current) {
      // Answer just arrived — scroll to the start of the answer
      lastAnswerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [displayThread.length, submitting, isEmpty]);

  // ── Ask (POST + poll) ────────────────────────────────────────────────────
  async function ask() {
    const clean = String(question || "").trim();
    if (!clean || submitting) return;

    setPendingUserText(clean);
    setFreshAssistantMsg(null);
    setSubmitting(true);
    setError("");
    setQuestion("");

    let resolvedSessionId = sessionId || null;
    try {
      // 1. POST creates the job — server returns immediately and processes
      //    all stages in the background.
      const postRes = await adminPostJson("/api/admin/intelligence/chat", {
        session_id: sessionId || null,
        question: clean,
      });
      resolvedSessionId = postRes.session_id;

      // 2. New session created — tell the parent but keep our overlays.
      if (resolvedSessionId !== sessionId) {
        ownAskRef.current = true;
        onSessionStarted("tab1", resolvedSessionId);
      }

      // 3. Poll GET /sessions/:id every 2.5s until complete or error.
      //    Each poll includes a cache-busting _t param to prevent browser caching.
      //    After ~15s (6 polls), switch to ?advance=step as a fallback in case the
      //    background fire-and-forget job died (e.g. Heroku dyno restart). The "step"
      //    mode advances at most 1 stage per request to stay within the 30s timeout.
      //    Hard cap at ~120s (48 polls) to avoid infinite spinning.
      activeAskRef.current = resolvedSessionId;
      let done = false;
      let pollCount = 0;
      let consecutiveErrors = 0;
      const FALLBACK_THRESHOLD = 6; // ~15s before fallback kicks in
      const MAX_POLLS = 48;          // ~120s hard cap
      while (!done) {
        await new Promise((r) => setTimeout(r, 2500));
        // Stop polling if user navigated away or component unmounted.
        if (activeAskRef.current !== resolvedSessionId) return;
        pollCount += 1;
        if (pollCount > MAX_POLLS) {
          setError("Request is taking too long. Your answer may still be processing — try refreshing or switching back to this conversation shortly.");
          setPendingUserText("");
          onSessionResolved("tab1", resolvedSessionId);
          break;
        }
        const advanceParam = pollCount >= FALLBACK_THRESHOLD ? "step" : "false";
        try {
          const res = await adminGetJson(
            `/api/admin/intelligence/sessions/${encodeURIComponent(resolvedSessionId)}?advance=${advanceParam}&_t=${Date.now()}`
          );
          consecutiveErrors = 0;
          if (activeAskRef.current !== resolvedSessionId) return;

          if (res.session?.status === "complete") {
            done = true;
            const msgs = res.messages || [];
            const lastAssistant = [...msgs].reverse().find((m) => m.role === "assistant");
            if (lastAssistant) setFreshAssistantMsg(lastAssistant);
            onSessionResolved("tab1", resolvedSessionId);
          } else if (res.session?.status === "error") {
            done = true;
            setError(res.session.error_message || "An error occurred. Please try again.");
            setPendingUserText("");
            onSessionResolved("tab1", resolvedSessionId);
          }
          // else still "running" — loop again
        } catch (_pollErr) {
          consecutiveErrors += 1;
          // After 5 consecutive failures, surface the error instead of spinning forever.
          if (consecutiveErrors >= 5) {
            setError("Lost connection while waiting for results. Please try again.");
            setPendingUserText("");
            onSessionResolved("tab1", resolvedSessionId);
            break;
          }
        }
      }
    } catch (err) {
      setError(err.message || String(err));
      setPendingUserText("");
      if (resolvedSessionId) onSessionResolved("tab1", resolvedSessionId);
    } finally {
      if (activeAskRef.current === resolvedSessionId) activeAskRef.current = null;
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>

      {/* ── Sidebar-navigation loading overlay ─────────────────────────────
          Covers the entire chat area with a centered spinner. Not shown while
          we are actively processing an Ask (submitting state handles that). */}
      {showLoadingOverlay ? (
        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.82)", zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <AdminSpinner loading />
          <div style={{ fontWeight: 700, color: "#334155" }}>Loading conversation…</div>
        </div>
      ) : null}

      {/* ── Message thread ────────────────────────────────────────────────── */}
      <div
        ref={listRef}
        style={{ flex: 1, overflowY: "auto", padding: "8px 8px 0 8px" }}
      >
        {/* Inner flex column — justifyContent: flex-end pins messages to
            bottom when there are only a few, exactly like ChatGPT/Claude. */}
        <div style={{ display: "flex", flexDirection: "column", justifyContent: isEmpty ? "flex-start" : "flex-end", minHeight: "100%", gap: 14, paddingBottom: 8 }}>
          {isEmpty ? (
            <div style={{ ...cardStyle, maxWidth: 920 }}>
              <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 10 }}>TenderManagers Data Intelligence</div>
              <div style={{ ...muted, fontSize: 15, lineHeight: 1.7, maxWidth: 760 }}>
                Ask anything about government procurement data. This assistant has access to GeM bids, CPPP tenders, bid results, contracts, seller history, pricing trends, and ministry and department spending patterns.
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
                {WELCOME_QUESTIONS.map((item) => (
                  <button key={item} style={{ ...buttonStyle, fontSize: 13 }} onClick={() => setQuestion(item)}>{item}</button>
                ))}
              </div>
            </div>
          ) : null}

          {displayThread.map((message, idx) => message.role === "user" ? (
            <div key={message.id} style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ maxWidth: "75%", background: "#dbeafe", color: "#0f172a", borderRadius: 18, padding: "12px 14px", fontWeight: 600, lineHeight: 1.6 }}>{message.content}</div>
            </div>
          ) : (
            <div key={message.id} ref={idx === lastAssistantIdx ? lastAnswerRef : undefined}>
              <AssistantBubble message={message} onEntityClick={onEntityClick} />
            </div>
          ))}

          {submitting ? (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div style={{ ...cardStyle, width: "min(100%, 920px)", padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700 }}>
                  <AdminSpinner loading />
                  <span>Thinking…</span>
                </div>
                <div style={{ ...muted, marginTop: 6 }}>Analysing your data… {elapsed}s</div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Input bar ─────────────────────────────────────────────────────── */}
      <div style={{ borderTop: "1px solid #e5e7eb", background: "#fff", padding: "6px 10px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea
            style={{ ...textareaStyle, flex: 1, width: "auto", minHeight: 48, resize: "none" }}
            rows={1}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) ask(); }}
            placeholder="Ask anything about GeM bids, CPPP tenders, results, contracts, pricing, sellers, ministries, or departments."
          />
          <button
            style={{ ...primaryButtonStyle, minWidth: 80, height: 42, flexShrink: 0, fontSize: 13 }}
            onClick={ask}
            disabled={submitting || !String(question || "").trim()}
          >
            {submitting ? "Thinking…" : "Ask"}
          </button>
          <button style={{ border: 0, background: "transparent", padding: 0, color: "#2563eb", cursor: "pointer", fontWeight: 700, fontSize: 12, flexShrink: 0, whiteSpace: "nowrap" }} onClick={onNewSession}>+ New</button>
        </div>
        {error ? <div style={{ marginTop: 6, color: "#b91c1c", fontWeight: 700, fontSize: 13 }}>Error: {error}</div> : null}
      </div>
    </div>
  );
}
